package top.wcpe.allincore.ui.runtime.ir.call

import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.ui.runtime.internal.time.CalendarUnit
import top.wcpe.allincore.ui.runtime.internal.time.addToEpochMillis
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.internal.time.diffEpochMillis
import top.wcpe.allincore.ui.runtime.internal.time.formatEpochMillis
import top.wcpe.allincore.ui.runtime.internal.time.localDateDay
import top.wcpe.allincore.ui.runtime.internal.time.localDateDayOfWeek
import top.wcpe.allincore.ui.runtime.internal.time.localDateDayOfYear
import top.wcpe.allincore.ui.runtime.internal.time.localDateIso
import top.wcpe.allincore.ui.runtime.internal.time.localDateMonth
import top.wcpe.allincore.ui.runtime.internal.time.localDateWeekOfYear
import top.wcpe.allincore.ui.runtime.internal.time.localDateYear
import top.wcpe.allincore.ui.runtime.internal.time.localTimeHour
import top.wcpe.allincore.ui.runtime.internal.time.localTimeMinute
import top.wcpe.allincore.ui.runtime.internal.time.localTimeSecond
import top.wcpe.allincore.ui.runtime.internal.time.nanoTime
import top.wcpe.allincore.ui.runtime.internal.time.parseToEpochMillisOrNull
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.DslValueUtils
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import kotlin.jvm.JvmStatic
import kotlin.math.floor

/**
 * `time.*` / `date.*` 方法分派实现。
 *
 * 从 [CallDispatcher] 中独立出来，避免主分派器进一步膨胀。所有方法都用系统默认时区
 *（ZoneId.systemDefault），与 vanilla / `local.player.*` 字段口径一致。
 */
@Suppress("MagicNumber")
class TimeDateApiInvoker private constructor() {

    companion object {
        /** 单位名 → CalendarUnit；与 DSL `date.add / date.diff` 文档保持一致。 */
        private val UNIT_TABLE: Map<String, CalendarUnit> = buildUnitTable()

        private fun buildUnitTable(): Map<String, CalendarUnit> {
            val m = mutableMapOf<String, CalendarUnit>()
            m["ms"] = CalendarUnit.MILLIS
            m["milli"] = CalendarUnit.MILLIS
            m["millis"] = CalendarUnit.MILLIS
            m["second"] = CalendarUnit.SECONDS
            m["seconds"] = CalendarUnit.SECONDS
            m["minute"] = CalendarUnit.MINUTES
            m["minutes"] = CalendarUnit.MINUTES
            m["hour"] = CalendarUnit.HOURS
            m["hours"] = CalendarUnit.HOURS
            m["day"] = CalendarUnit.DAYS
            m["days"] = CalendarUnit.DAYS
            m["week"] = CalendarUnit.WEEKS
            m["weeks"] = CalendarUnit.WEEKS
            m["month"] = CalendarUnit.MONTHS
            m["months"] = CalendarUnit.MONTHS
            m["year"] = CalendarUnit.YEARS
            m["years"] = CalendarUnit.YEARS
            return m
        }

        @JvmStatic
        fun invokeTime(method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? = when (method) {
            "now" -> {
                requireArgCount("time.now()", argNodes, 0)
                currentTimeMillis().toDouble()
            }
            "nowSeconds" -> {
                requireArgCount("time.nowSeconds()", argNodes, 0)
                floor(currentTimeMillis() / 1000.0)
            }
            "nanoTime" -> {
                requireArgCount("time.nanoTime()", argNodes, 0)
                nanoTime().toDouble()
            }
            "second" -> {
                requireArgCount("time.second()", argNodes, 0)
                localTimeSecond().toDouble()
            }
            "minute" -> {
                requireArgCount("time.minute()", argNodes, 0)
                localTimeMinute().toDouble()
            }
            "hour" -> {
                requireArgCount("time.hour()", argNodes, 0)
                localTimeHour().toDouble()
            }
            "format" -> {
                requireArgCount("time.format()", argNodes, 2)
                val ms = longArg("time.format()", argNodes, 0, ctx)
                val pattern = stringArg("time.format()", argNodes, 1, ctx)
                formatInstant(ms, pattern)
            }
            "elapsed" -> {
                requireArgCount("time.elapsed()", argNodes, 1)
                val since = longArg("time.elapsed()", argNodes, 0, ctx)
                (currentTimeMillis() - since).toDouble()
            }
            else -> throw DslRuntimeError("TimeApi 不支持的方法: $method")
        }

        @JvmStatic
        fun invokeDate(method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? = when (method) {
            "now", "today" -> {
                requireArgCount("date.$method()", argNodes, 0)
                localDateIso()
            }
            "year" -> {
                requireArgCount("date.year()", argNodes, 0)
                localDateYear().toDouble()
            }
            "month" -> {
                requireArgCount("date.month()", argNodes, 0)
                localDateMonth().toDouble()
            }
            "day" -> {
                requireArgCount("date.day()", argNodes, 0)
                localDateDay().toDouble()
            }
            "dayOfWeek" -> {
                requireArgCount("date.dayOfWeek()", argNodes, 0)
                localDateDayOfWeek().toDouble()
            }
            "weekOfYear" -> {
                requireArgCount("date.weekOfYear()", argNodes, 0)
                localDateWeekOfYear().toDouble()
            }
            "dayOfYear" -> {
                requireArgCount("date.dayOfYear()", argNodes, 0)
                localDateDayOfYear().toDouble()
            }
            "format" -> {
                requireArgCount("date.format()", argNodes, 2)
                val ms = longArg("date.format()", argNodes, 0, ctx)
                val pattern = stringArg("date.format()", argNodes, 1, ctx)
                formatInstant(ms, pattern)
            }
            "parse" -> {
                requireArgCount("date.parse()", argNodes, 2)
                val text = stringArg("date.parse()", argNodes, 0, ctx)
                val pattern = stringArg("date.parse()", argNodes, 1, ctx)
                parseToMillis(text, pattern)
            }
            "add" -> {
                requireArgCount("date.add()", argNodes, 3)
                val ms = longArg("date.add()", argNodes, 0, ctx)
                val amount = longArg("date.add()", argNodes, 1, ctx)
                val unit = stringArg("date.add()", argNodes, 2, ctx)
                addOnMillis(ms, amount, unit).toDouble()
            }
            "diff" -> {
                requireArgCount("date.diff()", argNodes, 3)
                val ms1 = longArg("date.diff()", argNodes, 0, ctx)
                val ms2 = longArg("date.diff()", argNodes, 1, ctx)
                val unit = stringArg("date.diff()", argNodes, 2, ctx)
                diffOnMillis(ms1, ms2, unit).toDouble()
            }
            else -> throw DslRuntimeError("DateApi 不支持的方法: $method")
        }

        private fun formatInstant(ms: Long, pattern: String): String = try {
            formatEpochMillis(ms, pattern)
        } catch (e: IllegalArgumentException) {
            throw DslRuntimeError("格式化模式非法: '$pattern' —— ${e.message}")
        }

        private fun parseToMillis(text: String, pattern: String): Double {
            val ms = try {
                parseToEpochMillisOrNull(text, pattern)
            } catch (e: IllegalArgumentException) {
                throw DslRuntimeError("格式化模式非法: '$pattern' —— ${e.message}")
            }
            return ms?.toDouble()
                ?: throw DslRuntimeError("date.parse() 解析失败：text='$text' pattern='$pattern' —— 无法按该模式解析")
        }

        private fun addOnMillis(ms: Long, amount: Long, unit: String): Long =
            addToEpochMillis(ms, amount, lookupUnit(unit))

        private fun diffOnMillis(ms1: Long, ms2: Long, unit: String): Long = diffEpochMillis(ms1, ms2, lookupUnit(unit))

        private fun lookupUnit(unit: String): CalendarUnit {
            val cu = UNIT_TABLE[unit] ?: throw DslRuntimeError(
                "未知时间单位: '$unit'（合法值：ms / second / minute / hour / day / week / month / year）",
            )
            return cu
        }

        private fun longArg(name: String, argNodes: List<ExprNode>, index: Int, ctx: EvalContext): Long {
            val value = ExprEvaluator.eval(argNodes[index], ctx)
            if (value !is Number) {
                throw DslRuntimeError("$name 第 ${index + 1} 个参数必须为 Number，实际类型: ${DslValueUtils.classOf(value)}")
            }
            return value.toLong()
        }

        private fun stringArg(name: String, argNodes: List<ExprNode>, index: Int, ctx: EvalContext): String {
            val value = ExprEvaluator.eval(argNodes[index], ctx)
            if (value !is String) {
                throw DslRuntimeError("$name 第 ${index + 1} 个参数必须为 String，实际类型: ${DslValueUtils.classOf(value)}")
            }
            return value
        }

        private fun requireArgCount(name: String, argNodes: List<ExprNode>, expected: Int) {
            if (argNodes.size != expected) {
                throw DslRuntimeError("$name 需要 $expected 个参数，实际 ${argNodes.size}")
            }
        }
    }
}
